import { Component, Input, Output, EventEmitter, forwardRef, HostListener, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';

interface SelectOption {
  value: any;
  label: string;
}

interface SelectOptionGroup {
  label: string;
  items: SelectOption[];
}

@Component({
  selector: 'app-select',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './app-select.component.html',
  styleUrls: ['./app-select.component.css'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AppSelectComponent),
    multi: true
  }]
})
export class AppSelectComponent implements ControlValueAccessor {
  @Input() label: string = '';
  @Input() placeholder: string = 'Seleccionar...';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() id: string = '';

  @Input() options: SelectOption[] = [];
  @Input() optionGroups: SelectOptionGroup[] = [];
  @Input() columns: number = 1;

  @Output() changeEvent = new EventEmitter<any>();

  innerValue: any = '';
  isOpen: boolean = false;
  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  constructor(private elementRef: ElementRef) {}

  get value(): any { return this.innerValue; }
  set value(val: any) {
    this.innerValue = val;
    this.onChange(val);
    this.onTouched();
  }

  writeValue(value: any): void {
    this.innerValue = value;
  }
  registerOnChange(fn: (value: any) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState?(isDisabled: boolean): void { this.disabled = isDisabled; }

  get selectedLabel(): string {
    if (this.options && this.options.length > 0) {
      const found = this.options.find(o => o.value === this.innerValue);
      if (found) return found.label;
    }
    if (this.optionGroups && this.optionGroups.length > 0) {
      for (const group of this.optionGroups) {
        const found = group.items.find(o => o.value === this.innerValue || o.label === this.innerValue);
        if (found) return found.label;
      }
    }
    return this.innerValue ? String(this.innerValue) : this.placeholder;
  }

  get hasGroups(): boolean {
    return this.optionGroups && this.optionGroups.length > 0;
  }

  toggleDropdown(): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
  }

  selectOption(option: SelectOption): void {
    this.value = option.value;
    this.changeEvent.emit(option.value);
    this.isOpen = false;
  }

  selectByLabel(label: string): void {
    this.value = label;
    this.changeEvent.emit(label);
    this.isOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }
}